import { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { handleFilesSelected } from '../../../../utils/fileUtils';
import styles from './MessageController.module.scss';
import cx from 'classnames';
import { Icon } from 'components/';
import { appActions } from 'store/app/slice';
import { selectIsPatientPageEdited, selectPatientActionIsLoading } from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';
import { TypeOfMessages } from 'types/messages';
import { getLinksFromMessage } from 'utils';

export const MessagesController = ({ isEnabled, patientCaseID, setCurrentTab }: any) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [message, setMessage] = useState('');
  const dispatch = useDispatch();

  const isSendingMessage = useSelector(selectPatientActionIsLoading('sendPatientMessage'));
  const isPatientPageEdited = useSelector(selectIsPatientPageEdited);

  useEffect(() => {
    if (inputRef.current) {
      const limit = 100;
      inputRef.current.style.height = '0px';
      const inputScrollHeight = inputRef.current.scrollHeight;
      inputRef.current.style.height = Math.min(inputScrollHeight, limit) + 'px';
    }
  }, [message]);

  useEffect(() => {
    setMessage('');
  }, [patientCaseID]);

  useEffect(() => {
    return () => {
      dispatch(patientsActions.setShouldPatientPageUpdate(true));
      dispatch(patientsActions.setShouldMessagesPageUpdate(true));
    };
  }, []);

  const promisifyLinks = (arr: string[]) => {
    return arr.map((el) => {
      return dispatch(
        patientsActions.sendPatientMessage({
          messageData: { attachmentLink: el, type: TypeOfMessages.LINK },
          patientCaseId: patientCaseID,
        }),
      );
    });
  };

  const onSubmit = async (message: string) => {
    if (!message || isSendingMessage) return;

    dispatch(patientsActions.setShouldMessagesPageUpdate(true));

    const links = getLinksFromMessage(message);
    let isSend;

    try {
      if (links && links.length === 1 && message.trim() === links[0]) {
        const wrappedResult = dispatch(
          patientsActions.sendPatientMessage({
            messageData: { attachmentLink: message, type: TypeOfMessages.LINK },
            patientCaseId: patientCaseID,
          }),
        );
        isSend = (await wrappedResult.unwrap()).isSend;
      } else {
        const wrappedResult = dispatch(
          patientsActions.sendPatientMessage({
            messageData: { body: message, type: TypeOfMessages.TEXT },
            patientCaseId: patientCaseID,
          }),
        );

        isSend = (await wrappedResult.unwrap()).isSend;

        if (links) {
          const promises = promisifyLinks(links);
          await Promise.all([promises]);
        }
      }

      if (isSend) {
        setMessage('');
        dispatch(appActions.setModalErrorText({ name: 'messagingError', errorText: '' }));
        if (!isPatientPageEdited) {
          dispatch(patientsActions.setShouldPatientPageUpdate(true));
        }
      }
    } catch (error: any) {
      dispatch(appActions.setModalErrorText({ name: 'messagingError', errorText: error?.title }));
      dispatch(appActions.setIsModalOpened({ name: 'messagingErrorModal', isOpened: true }));
    }
  };

  const { getInputProps, getRootProps } = useDropzone({
    onDrop: (plainFiles) => handleFilesSelected(plainFiles, patientCaseID, dispatch),
  });

  const handleMessageInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    if (e.target.value) {
      dispatch(patientsActions.setShouldPatientPageUpdate(false));
      dispatch(patientsActions.setShouldMessagesPageUpdate(false));
    } else {
      if (!isPatientPageEdited) {
        dispatch(patientsActions.setShouldPatientPageUpdate(true));
      }
      dispatch(patientsActions.setShouldMessagesPageUpdate(true));
    }
  };

  return (
    <div className={styles.mobileWrapper}>
      <div
        className={cx(styles.messageControllers, {
          [styles.disabled]: !isEnabled || isSendingMessage,
        })}>
        {isEnabled ? (
          <div
            {...getRootProps({
              className: cx(styles.iconWrapper, styles.plusIconWrapper),
            })}>
            <input {...getInputProps()} />
            <Icon name="ChatInputPlus" />
          </div>
        ) : (
          <div className={cx(styles.iconWrapper, styles.plusIconWrapper)}>
            <Icon name="ChatInputPlus" />
          </div>
        )}
        <div className={styles.inputWrapper}>
          <textarea
            placeholder="Start typing..."
            name="message"
            value={message}
            onChange={handleMessageInput}
            disabled={!isEnabled || isSendingMessage}
            className={styles.messageInput}
            ref={inputRef}
          />
        </div>
        <div
          className={cx(styles.sendMessageButton, {
            [styles.active]: message && !isSendingMessage,
          })}>
          <Icon name="Send" className={styles.icon} onClick={() => onSubmit(message)} />
        </div>
      </div>
      <div className={styles.attachmentsIconWrapper} onClick={setCurrentTab}>
        <Icon name="Files" />
      </div>
    </div>
  );
};
